import React from 'react';
import PropTypes from 'prop-types';
import computeScrollIntoView from 'compute-scroll-into-view';
import { Wrapper } from './StyledMenu';

/** */
function isDomElement(element) {
  return (
    // eslint-disable-next-line no-eq-null
    element != null && typeof element === 'object' && element.nodeType === 1
  );
}

/**
 * Scroll the item element into view if need to
 */
function scrollNodeToView(itemEl, menuEl, scrollPosition) {
  if (!isDomElement(itemEl)) {
    return;
  }

  const actions = computeScrollIntoView(itemEl, {
    boundary: menuEl,
    block: scrollPosition,
    scrollMode: 'if-needed'
  });
  actions.forEach(({ el, top }) => {
    // eslint-disable-next-line no-param-reassign
    el.scrollTop = top;
  });
}

const { arrayOf, bool, func, node, oneOfType, string, oneOf } = PropTypes;

/** */
// eslint-disable-next-line react/no-unsafe
export default class MenuList extends React.PureComponent {
  static propTypes = {
    /**
     * aria-label
     */
    'aria-label': string,
    /**
     * Determine whether focus on a menu-item when Menu opens
     */
    // eslint-disable-next-line react/boolean-prop-naming
    autoFocus: bool,
    /**
     * children MenuItem to render.
     */
    children: oneOfType([arrayOf(node), node]),
    /**
     * Determine whether MenuItem can be searched by typing
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableSearch: bool,
    /**
     * Determine which search type to apply while typing
     * full - search sequentially adding typed symbold from the beginning
     * first - mimic all behavior to match first char only
     * partial - search for the first occurence in the middle of displayed items
     */
    searchMode: oneOf(['full', 'first', 'partial']),
    /**
     * ID to be applied to the menu.
     */
    id: string,
    /**
     * Determine the maximum width of Menu, unit is px.
     */
    maxHeight: string,
    /**
     * Determine the minimum width of Menu, unit is px.
     */
    minWidth: string,
    /**
     * An ARIA role to be assigned to Menu.
     */
    menuRole: string,
    /**
     * Function to call on key down event.
     */
    onKeyDown: func,
    /**
     * reference: callback that returns the ref for menu list
     */
    reference: func,
    /**
     * Scroll current selected item into view from Menu if necessary
     */
    // eslint-disable-next-line react/boolean-prop-naming
    scrollToSelected: bool
  };

  static defaultProps = {
    'aria-label': null,
    autoFocus: false,
    children: undefined,
    disableSearch: false,
    searchMode: 'first',
    id: undefined,
    minWidth: undefined,
    maxHeight: undefined,
    menuRole: 'menu',
    onKeyDown() {},
    reference() {},
    scrollToSelected: false
  };

  constructor(props) {
    super(props);
    const [focusedItemIndex] = this.findAllMatched(
      props.children,
      child => !child.props.disabled
    );
    this.focusedItemIndex = focusedItemIndex ? 0 : focusedItemIndex;
    this.selectedIndices = [];
    this.searchString = '';
    this.menuListRef = React.createRef();
    this.state = { activeDescendant: '' };
  }

  componentDidMount() {
    this.props.reference(this.menuListRef.current);
    if (this.props.autoFocus) this.setMenuItemFocus();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.autoFocus) {
      this.setFocusOnSelectedItem(nextProps);
    }
  }

  componentDidUpdate() {
    if (this.props.autoFocus && this.focusedItemIndex) {
      this.menuListRef.current.children[this.focusedItemIndex].focus();
    }

    if (this.props.scrollToSelected) {
      this.scrollItemToView(this.props);
    }
  }

  setFocusOnSelectedItem(props = this.props) {
    const selectedIndices = this.findAllMatched(
      props.children,
      child => child.props.selected
    );
    const [lastSelected] = this.findArraysDifference(
      this.selectedIndices,
      selectedIndices
    );
    this.selectedIndices = selectedIndices;
    this.focusedItemIndex = lastSelected || this.focusedItemIndex;
    if (this.focusedItemIndex) {
      this.setMenuItemFocus();
    }
  }

  setFocusOnFirstMatchItem(event) {
    const { searchMode } = this.props;
    const searchChar = event.key;
    if (!['full', 'partial'].includes(searchMode)) this.searchString = '';
    const searchString = `${this.searchString.toLowerCase()}${searchChar.toLowerCase()}`;
    this.searchString = searchString;
    let selectedIndices = [];
    if (['partial'].includes(searchMode)) {
      selectedIndices = this.findAllMatched(
        this.props.children,
        child =>
          !child.props.disabled &&
          ((child.props.children &&
            typeof child.props.children === 'string' &&
            child.props.children.toLowerCase().includes(searchString)) ||
            (child.props.label &&
              child.props.label.toLowerCase().includes(searchString)))
      );
    } else {
      selectedIndices = this.findAllMatched(
        this.props.children,
        child =>
          !child.props.disabled &&
          ((child.props.children &&
            typeof child.props.children === 'string' &&
            child.props.children.toLowerCase().startsWith(searchString)) ||
            (child.props.label &&
              child.props.label.toLowerCase().startsWith(searchString)))
      );
    }

    const [matchIndex] = selectedIndices;
    this.selectedIndices = selectedIndices;
    if (matchIndex) {
      this.focusedItemIndex = matchIndex;
      this.setMenuItemFocus();
    }
  }

  setMenuItemFocus() {
    const { children } = this.props;
    const focusedMenuItem = this.menuListRef.current.children[
      this.focusedItemIndex
    ];
    if (focusedMenuItem && !children[this.focusedItemIndex].props.disabled) {
      this.setState({ activeDescendant: focusedMenuItem.id }, () => {
        focusedMenuItem.focus();
      });
    }
  }

  scrollItemToView(props = this.props) {
    const [highlightIndex] = this.findAllMatched(
      props.children,
      child => child.props.highlighted
    );
    if (highlightIndex) {
      scrollNodeToView(
        this.menuListRef.current.children[highlightIndex],
        this.menuListRef.current,
        'nearest'
      );
    } else {
      const selectedIndices = this.findAllMatched(
        props.children,
        child => child.props.selected
      );
      const [lastSelected] = this.findArraysDifference(
        this.selectedIndices,
        selectedIndices
      );
      this.selectedIndices = selectedIndices;
      if (lastSelected) {
        scrollNodeToView(
          this.menuListRef.current.children[lastSelected],
          this.menuListRef.current,
          'center'
        );
      }
    }
  }

  findAllMatched = (array, check) => {
    const arrayOfSelectedIndices = [];
    array.forEach((child, index) => {
      if (check(child)) {
        arrayOfSelectedIndices.push(index);
      }
    });
    return arrayOfSelectedIndices;
  };

  findArraysDifference = (a, b) => {
    let a1;
    let b1;
    if (a.length > b.lenth) {
      b1 = b;
      a1 = a;
    } else {
      b1 = a;
      a1 = b;
    }

    return a1.filter(x => !b1.includes(x));
  };

  handleArrowKeyDown(event) {
    event.preventDefault();
    const { key } = event;
    const { children } = this.props;
    let count = 0;
    do {
      if (key === 'ArrowDown') {
        this.focusedItemIndex += 1;
      }

      if (key === 'ArrowUp') {
        this.focusedItemIndex -= 1;
      }

      if (this.focusedItemIndex < 0) {
        this.focusedItemIndex = children.length - 1;
      }

      if (this.focusedItemIndex === children.length) {
        this.focusedItemIndex = 0;
      }

      count += 1;
    } while (
      this.focusedItemIndex &&
      children[this.focusedItemIndex].props.disabled &&
      count < children.length
    );

    this.setMenuItemFocus();
  }

  handleAccessibilityKeySupport(event) {
    event.persist();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        this.searchString = '';
        this.handleArrowKeyDown(event);
        break;
      case 'Home':
        event.preventDefault();
        this.focusedItemIndex = 0;
        this.searchString = '';
        this.setMenuItemFocus();
        break;
      case 'End':
        event.preventDefault();
        this.focusedItemIndex = this.props.children.length - 1;
        this.searchString = '';
        this.setMenuItemFocus();
        break;
      default:
        if (!this.props.disableSearch) {
          this.setFocusOnFirstMatchItem(event);
        }

        break;
    }
  }

  handleKeyDown = event => {
    this.handleAccessibilityKeySupport(event);
    this.props.onKeyDown(event);
  };

  render() {
    const {
      children,
      disableSearch,
      maxHeight,
      minWidth,
      menuRole,
      reference,
      scrollToSelected,
      ...restProps
    } = this.props;
    if (this.state.activeDescendant) {
      restProps['aria-activedescendant'] = this.state.activeDescendant;
    }

    return (
      <Wrapper
        {...restProps}
        ref={this.menuListRef}
        maxHeight={maxHeight}
        minWidth={minWidth}
        role={menuRole}
        tabIndex="-1"
        onKeyDown={this.handleKeyDown}
      >
        {children}
      </Wrapper>
    );
  }
}
